import { useMemo, useState } from 'react';
import { addExpense, updateExpense } from '../lib/db';
import { track } from '../lib/analytics';
import { curOf, money, todayISO, toInput, toMinor, wfmt } from '../lib/money';
import { myUnit, paymentsFor, sharesFor, unitWeightTotal } from '../lib/split';
import { uploadReceipt, receiptsEnabled } from '../lib/receipts';
import { CATEGORIES, Icon } from '../components/Icons';
import { ColorDot, Note, Segmented, Sheet, Toggle, UnitAvatar } from '../components/ui';
import type { Expense, SplitBasis, TripData } from '../types';

const toInputs = (m: Record<string, number> | undefined): Record<string, string> =>
  Object.fromEntries(Object.entries(m ?? {}).map(([k, v]) => [k, toInput(v)]));

/** Adds an expense, or edits one when `expense` is given. */
export default function AddExpense({ data, uid, expense: editing, onClose, onSaved }: {
  data: TripData;
  uid: string;
  expense?: Expense;
  onClose: () => void;
  onSaved: (label: string) => void;
}) {
  const { group, units } = data;
  const symbol = curOf(group.currency).symbol;
  const mine = myUnit(units, uid);

  // The amount field is what the paying household paid. When others covered
  // part of it, that's less than the expense total — see amountMinor below.
  const [amount, setAmount] = useState(
    editing ? toInput(paymentsFor(editing)[editing.paidByUnitId] ?? editing.amountMinor) : ''
  );
  const [desc, setDesc] = useState(editing?.desc ?? '');
  const [category, setCategory] = useState(editing?.category ?? 'food');
  const [paidBy, setPaidBy] = useState(editing?.paidByUnitId ?? mine?.id ?? units[0]?.id ?? '');
  const [basis, setBasis] = useState<SplitBasis>(editing?.basis ?? group.defaultBasis ?? 'perPerson');
  const [unitIds, setUnitIds] = useState<string[]>(
    editing?.basis === 'perUnit' ? editing.participantUnitIds : units.map((u) => u.id)
  );
  const [personIds, setPersonIds] = useState<string[]>(
    editing?.basis === 'perPerson' ? editing.participantPersonIds
      : units.flatMap((u) => u.people.filter((p) => p.weight > 0).map((p) => p.id))
  );
  const [custom, setCustom] = useState<Record<string, string>>(
    editing?.basis === 'custom' ? toInputs(editing.customMinor) : {}
  );
  const [coverOn, setCoverOn] = useState(Object.keys(editing?.coveredMinor ?? {}).length > 0);
  const [covered, setCovered] = useState<Record<string, string>>(toInputs(editing?.coveredMinor));
  const [date, setDate] = useState(editing?.date ?? todayISO());
  const [note, setNote] = useState(editing?.note ?? '');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paidMinor = toMinor(amount);
  const customMinor = useMemo(() => {
    const out: Record<string, number> = {};
    for (const k of Object.keys(custom)) {
      const v = toMinor(custom[k]);
      if (v) out[k] = v;
    }
    return out;
  }, [custom]);

  // What other households already paid towards this. The payer can't cover
  // part of their own expense, so switching payer quietly drops their entry.
  const coveredMinor = useMemo(() => {
    const out: Record<string, number> = {};
    if (!coverOn) return out;
    for (const u of units) {
      const v = u.id === paidBy ? 0 : toMinor(covered[u.id] ?? '');
      if (v) out[u.id] = v;
    }
    return out;
  }, [coverOn, covered, units, paidBy]);
  const coveredTotal = Object.values(coveredMinor).reduce((a, b) => a + b, 0);
  // The expense total: what the payer paid plus what others covered. A $49
  // coupon on top of $81 paid is a $130 ticket, and $130 is what gets split.
  const amountMinor = paidMinor + coveredTotal;
  const payerName = units.find((u) => u.id === paidBy)?.name ?? 'The payer';

  // A throwaway expense object so the preview runs through the real engine.
  const draft: Expense = useMemo(() => ({
    id: 'draft', desc, amountMinor, category, date,
    paidByUnitId: paidBy, paidByUid: uid, basis,
    participantPersonIds: personIds, participantUnitIds: unitIds,
    customMinor, coveredMinor, createdBy: uid
  }), [desc, amountMinor, category, date, paidBy, uid, basis, personIds, unitIds, customMinor, coveredMinor]);

  const shares = sharesFor(units, draft);
  const assigned = Object.values(shares).reduce((a, b) => a + b, 0);
  const balanced = amountMinor > 0 && assigned === amountMinor;
  // A cleared date input gives "", which would drop the expense out of every day list.
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const canSave = balanced && paidMinor > 0 && dateValid && desc.trim().length > 0
    && Boolean(paidBy) && !saving;

  function togglePerson(id: string) {
    setPersonIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }
  function toggleUnit(id: string) {
    setUnitIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }
  function toggleWholeUnit(unitId: string) {
    const u = units.find((x) => x.id === unitId);
    if (!u) return;
    const ids = u.people.filter((p) => p.weight > 0).map((p) => p.id);
    const allOn = ids.length > 0 && ids.every((id) => personIds.includes(id));
    setPersonIds((cur) =>
      allOn ? cur.filter((x) => !ids.includes(x)) : Array.from(new Set([...cur, ...ids]))
    );
  }

  function pickReceipt(file: File | null) {
    setReceipt(file);
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return file ? URL.createObjectURL(file) : null;
    });
  }

  function switchBasis(next: SplitBasis) {
    // Seed custom amounts with an even split so the sheet opens balanced.
    if (next === 'custom' && Object.keys(custom).length === 0 && amountMinor > 0) {
      const even = sharesFor(units, { ...draft, basis: 'perUnit', participantUnitIds: units.map((u) => u.id) });
      const seeded: Record<string, string> = {};
      for (const u of units) seeded[u.id] = ((even[u.id] ?? 0) / 100).toFixed(2);
      setCustom(seeded);
    }
    setBasis(next);
  }

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const fields = {
        desc: desc.trim(),
        amountMinor,
        category,
        date,
        paidByUnitId: paidBy,
        basis,
        participantPersonIds: basis === 'perPerson' ? personIds : [],
        participantUnitIds: basis === 'perUnit' ? unitIds : [],
        customMinor: basis === 'custom' ? customMinor : {},
        coveredMinor,
        note: note.trim()
      };
      // Editing keeps the existing receipt unless a new one is attached.
      let receiptFields: Pick<Expense, 'receiptUrl' | 'receiptPath'> = {};
      if (receipt && receiptsEnabled) {
        const up = await uploadReceipt(group.id, receipt);
        receiptFields = { receiptUrl: up.url, receiptPath: up.path };
      }
      if (editing) {
        await updateExpense(group.id, editing.id, { ...fields, ...receiptFields });
        track('expense_edited', { basis, covered: coveredTotal > 0 });
        onSaved('Expense updated');
      } else {
        await addExpense(group.id, uid, {
          ...fields, paidByUid: uid, receiptUrl: null, receiptPath: null, ...receiptFields
        });
        track('expense_added', { basis, covered: coveredTotal > 0, category });
        onSaved(`${money(amountMinor, group.currency)} added and split`);
      }
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Sheet
      title={editing ? 'Edit expense' : 'Add expense'}
      onClose={onClose}
      footer={
        <button className="btn" disabled={!canSave} onClick={() => void save()}>
          {saving ? 'Saving…'
            : editing ? 'Save changes'
              : amountMinor > 0 ? `Add ${money(amountMinor, group.currency)}` : 'Add expense'}
        </button>
      }
    >
      <div className="amount-field">
        <span>{symbol}</span>
        <input
          id="f-amt" inputMode="decimal" placeholder="0" autoFocus autoComplete="off"
          value={amount} onChange={(e) => setAmount(e.target.value)}
        />
      </div>
      {coveredTotal > 0 && paidMinor > 0 && (
        <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, margin: '8px 2px 0' }}>
          What {payerName} paid · {money(amountMinor, group.currency)} in total with what
          others covered
        </p>
      )}

      <div className="field">
        <label htmlFor="f-desc">What was it for</label>
        <input id="f-desc" className="inp" value={desc} autoComplete="off"
          placeholder="Dinner at Thalassa" onChange={(e) => setDesc(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="f-date">Date</label>
        <input id="f-date" className="inp" type="date" value={date}
          onChange={(e) => setDate(e.target.value)} />
      </div>

      <div className="field">
        <label>Category</label>
        <div className="chips">
          {CATEGORIES.map((c) => (
            <button key={c.id} className="chip" aria-pressed={category === c.id}
              onClick={() => setCategory(c.id)}>
              <Icon name={c.id} /> {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label>Paid by</label>
        <div className="chips">
          {units.map((u) => (
            <button key={u.id} className="chip" aria-pressed={paidBy === u.id}
              onClick={() => setPaidBy(u.id)}>
              <ColorDot color={u.color} /> {u.name}
            </button>
          ))}
        </div>

        {units.length > 1 && (
          <div className="custom-row" style={{ marginTop: 4 }}>
            <span className="nm">Another family covered part of it</span>
            <Toggle on={coverOn} label="Another family covered part of it" onChange={setCoverOn} />
          </div>
        )}

        {coverOn && (
          <>
            <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, margin: '0 2px 4px' }}>
              A coupon, gift card or deposit they paid for earlier. Enter what {payerName} paid
              as the amount above — what they covered is added to it, and the total is split.
            </p>
            {units.filter((u) => u.id !== paidBy).map((u) => (
              <div className="custom-row" key={u.id}>
                <ColorDot color={u.color} />
                <span className="nm">{u.name}</span>
                <input
                  className="ci" inputMode="decimal" placeholder="0"
                  aria-label={`Amount ${u.name} covered`}
                  value={covered[u.id] ?? ''}
                  onChange={(e) => setCovered((c) => ({ ...c, [u.id]: e.target.value }))}
                />
              </div>
            ))}
            {coveredTotal > 0 && (
              paidMinor > 0 ? (
                <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, margin: '6px 2px 0' }}>
                  {payerName} paid {money(paidMinor, group.currency)} + covered{' '}
                  {money(coveredTotal, group.currency)} = {money(amountMinor, group.currency)} total
                </p>
              ) : (
                <p style={{ color: 'var(--clay)', fontSize: 12.5, fontWeight: 600, margin: '6px 2px 0' }}>
                  Enter what {payerName} paid as the amount above — or, if others covered all
                  of it, make one of them the payer.
                </p>
              )
            )}
          </>
        )}
      </div>

      <div className="field">
        <label>How should it split</label>
        <Segmented<SplitBasis>
          value={basis}
          onChange={switchBasis}
          options={[
            { id: 'perPerson', label: 'Per person' },
            { id: 'perUnit', label: 'Per family' },
            { id: 'custom', label: 'Custom' }
          ]}
        />
      </div>

      <div className="field">
        <label>
          {basis === 'custom' ? 'Exact amounts' : basis === 'perUnit' ? 'Which families share this' : "Who's in"}
        </label>

        {basis === 'custom' && units.map((u) => (
          <div className="custom-row" key={u.id}>
            <ColorDot color={u.color} />
            <span className="nm">{u.name}</span>
            <input
              className="ci" inputMode="decimal" placeholder="0"
              value={custom[u.id] ?? ''}
              onChange={(e) => setCustom((c) => ({ ...c, [u.id]: e.target.value }))}
            />
          </div>
        ))}

        {basis === 'perUnit' && units.map((u) => (
          <div className="who-unit" key={u.id}>
            <div className="uh">
              <UnitAvatar unit={u} small />
              <span className="nm">{u.name}</span>
              <Toggle on={unitIds.includes(u.id)} label={`Include ${u.name}`}
                onChange={() => toggleUnit(u.id)} />
            </div>
          </div>
        ))}

        {basis === 'perPerson' && units.map((u) => {
          const included = u.people.filter((p) => personIds.includes(p.id));
          const w = included.reduce((a, p) => a + p.weight, 0);
          const countable = u.people.filter((p) => p.weight > 0);
          const allOn = countable.length > 0 && countable.every((p) => personIds.includes(p.id));
          return (
            <div className="who-unit" key={u.id}>
              <div className="uh">
                <UnitAvatar unit={u} small />
                <span className="nm">{u.name}</span>
                <span className="sh">{wfmt(w)} sh</span>
                <Toggle on={allOn && w > 0} label={`Include all of ${u.name}`}
                  onChange={() => toggleWholeUnit(u.id)} />
              </div>
              <div className="who-people">
                {u.people.map((p) => (
                  <button key={p.id} className="ptog" aria-pressed={personIds.includes(p.id)}
                    onClick={() => togglePerson(p.id)}>
                    {p.name}<span className="k">{p.weight === 0 ? '0' : wfmt(p.weight)}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* The live preview is what makes the household model legible. */}
      <div className="preview">
        <div className="ph">
          <span className="l">Each family pays</span>
          <span className="r">
            {amountMinor > 0 ? `${money(amountMinor, group.currency)} total` : 'enter an amount'}
          </span>
        </div>
        {units.map((u) => {
          const v = shares[u.id] ?? 0;
          const q = basis === 'perPerson'
            ? (() => {
                const w = u.people.filter((p) => personIds.includes(p.id))
                  .reduce((a, p) => a + p.weight, 0);
                return w ? `${wfmt(w)} sh` : '—';
              })()
            : basis === 'perUnit'
              ? (unitIds.includes(u.id) ? '1 sh' : '—')
              : '';
          return (
            <div className="pr" key={u.id}>
              <ColorDot color={u.color} />
              <span className="nm">{u.name}</span>
              <span className="wq">{q}</span>
              <span className={`vv ${v ? '' : 'muted'}`}>
                {v ? money(v, group.currency) : '—'}
              </span>
            </div>
          );
        })}
        {amountMinor > 0 && assigned !== amountMinor && (
          <div className="warn">
            {assigned > amountMinor
              ? `${money(assigned - amountMinor, group.currency)} over`
              : `${money(amountMinor - assigned, group.currency)} still unassigned`}
          </div>
        )}
      </div>

      <div className="field">
        <label htmlFor="f-note">Note (optional)</label>
        <input id="f-note" className="inp" value={note} autoComplete="off"
          placeholder="One room per family" onChange={(e) => setNote(e.target.value)} />
      </div>

      {receiptsEnabled && (
        <div className="field">
          <label>Receipt</label>
          {preview ? (
            <div className="receipt">
              <img src={preview} alt="Receipt preview" />
              <span>Receipt attached</span>
              <button className="x" onClick={() => pickReceipt(null)}>Remove</button>
            </div>
          ) : (
            <label className="receipt" style={{ cursor: 'pointer' }}>
              <Icon name="camera" />
              <span>Snap or attach the bill</span>
              <input
                type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => pickReceipt(e.target.files?.[0] ?? null)}
              />
            </label>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 14 }}>
          <Note>Couldn't save that: {error}</Note>
        </div>
      )}

      {units.length > 0 && unitWeightTotal(units[0]) === 0 && basis === 'perPerson' && (
        <div style={{ marginTop: 14 }}>
          <Note>Everyone in this trip is set to zero shares, so there's nothing to divide by.</Note>
        </div>
      )}
    </Sheet>
  );
}
